import classNames from "classnames";
import React, { useEffect, useRef, useState, type ReactNode } from "react";
import { Svg } from "../../Assets/Svg/React";

type CollapsibleVariant = "emergency" | "information";

interface CollapsibleSectionProps {
  children?: ReactNode;
  className?: string;
  labelClickToClose?: string;
  labelClickToOpen?: string;
  variant?: CollapsibleVariant;
}

const variantConfig = {
  emergency: {
    color: "text-t-pink",
    icon: "/svg/system/emergency.svg",
    bgColor: "bg-pink-200"
  },
  information: {
    color: "text-dark-blue",
    icon: "/svg/system/info.svg",
    bgColor: "bg-neutral-100"
  }
} as const;

export const CollapsibleSection = ({
  children,
  className,
  labelClickToClose,
  labelClickToOpen,
  variant = "information"
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState<string>("0px");
  const contentRef = useRef<HTMLDivElement>(null);
  const collapsibleRef = useRef<HTMLDivElement>(null);

  const config = variantConfig[variant];

  // Handle height animation
  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    if (isOpen) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(`${contentHeight}px`);
    } else {
      setHeight("0px");
    }
  }, [isOpen, children]);

  // Reset height to auto after transition completes (for dynamic content)
  useEffect(() => {
    if (!isOpen || !contentRef.current || !collapsibleRef.current) {
      return;
    }

    // Extract transition duration from computed style
    const computedStyle = window.getComputedStyle(collapsibleRef.current);
    const duration = computedStyle.transitionDuration;

    // Parse duration (can be in seconds like "0.5s" or milliseconds like "500ms")
    const durationMs = duration.includes("ms")
      ? parseFloat(duration)
      : parseFloat(duration) * 1000;

    const timer = setTimeout(() => {
      setHeight("auto");
    }, durationMs);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const toggleOpen = () => {
    // When closing, measure current height first
    if (isOpen && contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(`${contentHeight}px`);

      // Force reflow, then set to 0
      requestAnimationFrame(() => {
        setHeight("0px");
      });
    }

    setIsOpen(!isOpen);
  };

  return (
    <div
      className={classNames(
        "relative rounded-tl-br-md overflow-hidden",
        "focus-outline",
        "has-[[data-collapsible-button]:focus-visible]:outline",
        config.bgColor,
        className
      )}
    >
      {/* Toggle Button */}
      <button
        aria-expanded={isOpen}
        className={classNames(
          "cursor-pointer flex items-center w-full bg-transparent justify-between p-sm",
          "hover:bg-black/[0.03] transition-short-in-out focus:[outline-style:none]",
          config.color
        )}
        data-collapsible-button
        onClick={toggleOpen}
        type="button"
      >
        <span className="flex items-center gap-xs text-h4 normal-case">
          <Svg href={config.icon} />

          {isOpen ? labelClickToClose : labelClickToOpen}
        </span>

        <Svg
          href="/svg/system/chevron-down.svg"
          additionalClass={classNames("w-6 h-6 mr-sm transition-short-in-out", {
            "rotate-180": isOpen
          })}
        />
      </button>

      {/* Collapsible Content */}
      <div
        className="transition-[height] transition-short-in-out"
        {...{ inert: !isOpen ? "inert" : undefined }}
        ref={collapsibleRef}
        style={{
          height: height,
          overflow: height === "auto" ? "visible" : "hidden"
        }}
      >
        <div className="px-sm pb-sm" ref={contentRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
